import { SupportedLanguage } from "../services/language.service";
import { APP_NAME } from "./app.constants";

export interface ExplorePageContent {
  slug: string;
  eyebrow: string;
  title: string;
  description: string;
  introTitle: string;
  intro: string;
  points: { title: string; text: string }[];
  related: { label: string; url: string }[];
}

type Localized<T> = Record<SupportedLanguage, T>;

const localized = (
  english: ExplorePageContent,
  translations: Partial<Localized<ExplorePageContent>>,
): Localized<ExplorePageContent> => ({
  en: english,
  es: translations.es ?? english,
  de: translations.de ?? english,
  fr: translations.fr ?? english,
  it: translations.it ?? english,
  "pt-br": translations["pt-br"] ?? english,
  ja: translations.ja ?? english,
});

export const EXPLORE_PAGES: Readonly<
  Record<string, Localized<ExplorePageContent>>
> = {
  "chat-with-strangers": localized(
    {
      slug: "chat-with-strangers",
      eyebrow: "TEXT CHAT",
      title: "Chat with strangers online",
      description:
        "Start a free anonymous text conversation with someone new, without a public profile or a long signup flow.",
      introTitle: "A better first hello",
      intro: `${APP_NAME} makes meeting someone new feel simple. Choose a nickname, select interests if you want, and begin a one-to-one text chat with a random match.`,
      points: [
        {
          title: "Start without a profile",
          text: "Guest access lets you begin with only the information needed for a respectful chat.",
        },
        {
          title: "Use interests as an opening",
          text: "Shared topics are preferred, but you can still meet someone when interests do not overlap.",
        },
        {
          title: "Stay in control",
          text: "Skip, block, report, or leave whenever a conversation no longer feels right.",
        },
      ],
      related: [
        { label: "Random chat", url: "/random-chat" },
        { label: "Anonymous chat", url: "/anonymous-chat" },
        { label: "Safety", url: "/safety" },
      ],
    },
    {
      es: {
        slug: "chat-with-strangers",
        eyebrow: "CHAT DE TEXTO",
        title: "Chatea con desconocidos en línea",
        description:
          "Inicia una conversación de texto anónima y gratuita con alguien nuevo, sin perfil público ni registro largo.",
        introTitle: "Un mejor primer saludo",
        intro: `${APP_NAME} hace sencillo conocer a alguien nuevo. Elige un apodo, selecciona intereses si quieres y comienza un chat individual.`,
        points: [
          {
            title: "Empieza sin perfil",
            text: "El acceso de invitado te permite comenzar con solo la información necesaria.",
          },
          {
            title: "Encuentra intereses comunes",
            text: "Priorizamos temas compartidos, pero también puedes conocer personas diferentes.",
          },
          {
            title: "Tú tienes el control",
            text: "Puedes saltar, bloquear, denunciar o salir en cualquier momento.",
          },
        ],
        related: [
          { label: "Chat aleatorio", url: "/random-chat" },
          { label: "Chat anónimo", url: "/anonymous-chat" },
          { label: "Seguridad", url: "/safety" },
        ],
      },
      de: {
        slug: "chat-with-strangers",
        eyebrow: "TEXT-CHAT",
        title: "Online mit Fremden chatten",
        description:
          "Starte ein kostenloses anonymes Textgespräch mit jemandem, ohne öffentliches Profil und ohne lange Anmeldung.",
        introTitle: "Ein besseres erstes Hallo",
        intro: `${APP_NAME} macht neue Begegnungen einfach. Wähle einen Spitznamen, Interessen und beginne einen direkten Text-Chat.`,
        points: [
          {
            title: "Ohne Profil starten",
            text: "Der Gastzugang benötigt nur die Informationen für einen respektvollen Chat.",
          },
          {
            title: "Gemeinsame Themen finden",
            text: "Gemeinsame Interessen werden bevorzugt, sind aber keine Voraussetzung.",
          },
          {
            title: "Du bestimmst",
            text: "Überspringen, blockieren, melden oder jederzeit gehen.",
          },
        ],
        related: [
          { label: "Zufälliger Chat", url: "/random-chat" },
          { label: "Anonymer Chat", url: "/anonymous-chat" },
          { label: "Sicherheit", url: "/safety" },
        ],
      },
    },
  ),
  "random-chat": localized(
    {
      slug: "random-chat",
      eyebrow: "RANDOM CHAT",
      title: "Random chat with people worldwide",
      description:
        "Discover a new perspective through an anonymous text conversation shaped by shared interests.",
      introTitle: "A little randomness, with more control",
      intro: `Random chat should create possibility, not pressure. ${APP_NAME} helps you meet someone new while keeping the decision to continue in your hands.`,
      points: [
        {
          title: "Worldwide conversations",
          text: "Meet people from different places and backgrounds through a simple text connection.",
        },
        {
          title: "Matching with context",
          text: "Interests help prioritize a useful opening topic without excluding everyone else.",
        },
        {
          title: "Easy next steps",
          text: "Skip a match, start again, or leave the conversation whenever you choose.",
        },
      ],
      related: [
        { label: "Chat with strangers", url: "/chat-with-strangers" },
        { label: "Make friends online", url: "/make-friends-online" },
        { label: "Explore the Blog", url: "/blog" },
      ],
    },
    {
      es: {
        slug: "random-chat",
        eyebrow: "CHAT ALEATORIO",
        title: "Chat aleatorio con personas de todo el mundo",
        description:
          "Descubre nuevas perspectivas mediante una conversación de texto anónima basada en intereses compartidos.",
        introTitle: "Un poco de azar, con más control",
        intro:
          "El chat aleatorio debe crear posibilidades, no presión. Tú decides si quieres continuar.",
        points: [
          {
            title: "Conversaciones globales",
            text: "Conoce personas de lugares y culturas diferentes.",
          },
          {
            title: "Intereses como contexto",
            text: "Los intereses ayudan a encontrar un tema para empezar.",
          },
          {
            title: "Siguientes pasos fáciles",
            text: "Puedes saltar, volver a buscar o salir cuando quieras.",
          },
        ],
        related: [
          { label: "Chatear con desconocidos", url: "/chat-with-strangers" },
          { label: "Hacer amigos", url: "/make-friends-online" },
          { label: "Blog", url: "/blog" },
        ],
      },
    },
  ),
  "anonymous-chat": localized(
    {
      slug: "anonymous-chat",
      eyebrow: "PRIVATE BY DEFAULT",
      title: "Anonymous chat without a public profile",
      description:
        "Talk to strangers online with a nickname instead of a public profile. Share only what feels right for you.",
      introTitle: "Privacy makes the first conversation easier",
      intro: `You should not need a polished identity to say hello. ${APP_NAME} keeps the starting point lightweight and lets you decide what to share.`,
      points: [
        {
          title: "No public profile",
          text: "Start as a guest with a display name instead of building a social identity.",
        },
        {
          title: "Text-first by design",
          text: "Begin with words. A camera is not required for the current experience.",
        },
        {
          title: "Clear safety controls",
          text: "Block or report a user and they will not return during your current session.",
        },
      ],
      related: [
        { label: "Safety guidance", url: "/safety" },
        { label: "Random text chat", url: "/random-text-chat" },
        { label: "How it works", url: "/how-it-works" },
      ],
    },
    {
      fr: {
        slug: "anonymous-chat",
        eyebrow: "PRIVÉ PAR DÉFAUT",
        title: "Chat anonyme sans profil public",
        description:
          "Parlez à des inconnus avec un pseudonyme plutôt qu'un profil public. Partagez seulement ce qui vous convient.",
        introTitle: "La confidentialité facilite le premier échange",
        intro: `Vous n'avez pas besoin d'une identité parfaite pour dire bonjour. ${APP_NAME} garde le début simple et vous choisissez ce que vous partagez.`,
        points: [
          {
            title: "Pas de profil public",
            text: "Commencez avec un pseudonyme au lieu de créer une identité sociale.",
          },
          {
            title: "Pensé pour le texte",
            text: "Commencez par les mots. La caméra n'est pas nécessaire.",
          },
          {
            title: "Contrôles de sécurité",
            text: "Bloquez ou signalez un utilisateur pour ne plus le revoir pendant la session.",
          },
        ],
        related: [
          { label: "Conseils de sécurité", url: "/safety" },
          { label: "Chat texte aléatoire", url: "/random-text-chat" },
          { label: "Fonctionnement", url: "/how-it-works" },
        ],
      },
    },
  ),
  "make-friends-online": localized(
    {
      slug: "make-friends-online",
      eyebrow: "FIND YOUR PEOPLE",
      title: "Make friends online through real conversations",
      description:
        "Meet kind, curious people from different places and turn a random conversation into a new friendship.",
      introTitle: "Friendship can start with one unexpected chat",
      intro: `${APP_NAME} is for the moment before a connection has a name. Talk about music, languages, games, travel, or whatever makes the conversation feel natural.`,
      points: [
        {
          title: "Meet outside your circle",
          text: "A random match can introduce you to a perspective you would not find in your usual feed.",
        },
        {
          title: "Talk about what you enjoy",
          text: "Choose interests to make the first message easier and more relevant.",
        },
        {
          title: "Keep good boundaries",
          text: "A good conversation is mutual. Skip, block, or report when you need to.",
        },
      ],
      related: [
        { label: "Chat with strangers", url: "/chat-with-strangers" },
        { label: "Practice English", url: "/practice-english" },
        { label: "Safety", url: "/safety" },
      ],
    },
    {
      ja: {
        slug: "make-friends-online",
        eyebrow: "新しいつながり",
        title: "会話からオンラインで友達を作る",
        description:
          "世界中の好奇心旺盛な人と出会い、ランダムな会話を新しい友情につなげましょう。",
        introTitle: "友情は予想外の会話から始まる",
        intro: `${APP_NAME}では、音楽、言語、ゲーム、旅行など、自然な会話のきっかけを見つけられます。`,
        points: [
          {
            title: "いつもの環境の外へ",
            text: "普段のフィードでは出会えない視点に触れられます。",
          },
          {
            title: "好きなことを話す",
            text: "興味を選ぶと最初のメッセージが始めやすくなります。",
          },
          {
            title: "境界線を大切に",
            text: "必要なときはスキップ、ブロック、報告ができます。",
          },
        ],
        related: [
          { label: "知らない人とチャット", url: "/chat-with-strangers" },
          { label: "英語を練習", url: "/practice-english" },
          { label: "安全ガイド", url: "/safety" },
        ],
      },
    },
  ),
};
